import React, { useRef } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';

/**
 * NFCCard3D Component
 * Renders a realistic NFC card with:
 * - Transparent designed surface layer
 * - Technical components (chip, antenna, wiring) visible underneath
 * - Cross-section view showing the card's internal structure
 */
export default function NFCCard3D({
  color = '#18181b',
  hovered = false,
  rotation = [0.3, 0, 0],
  autoRotate = false
}) {
  const groupRef = useRef();
  const antennaRefs = useRef([]);

  // Detect mobile for performance optimization
  const [isMobile] = React.useState(() => {
    if (typeof window === 'undefined') return false;
    return window.innerWidth < 768 || /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  });

  // Auto-rotation and floating animation
  useFrame((state) => {
    if (groupRef.current) {
      // Floating animation (bobbing up and down)
      groupRef.current.position.y = Math.sin(state.clock.elapsedTime * 0.8) * 0.15;

      // Gentle continuous rotation
      if (autoRotate) {
        groupRef.current.rotation.y = Math.sin(state.clock.elapsedTime * 0.3) * 0.5;
      }

      // Additional subtle tilt animation
      groupRef.current.rotation.x = rotation[0] + Math.sin(state.clock.elapsedTime * 0.5) * 0.05;
    }

    // Enhanced antenna pulse animation
    if (antennaRefs.current) {
      antennaRefs.current.forEach((antenna, i) => {
        if (antenna) {
          const pulse = Math.sin(state.clock.elapsedTime * 2.5 + i * 0.8) * 0.5 + 0.5;
          const hoverBoost = hovered ? 1.5 : 1;
          antenna.material.emissiveIntensity = (0.4 + pulse * 0.8) * hoverBoost;

          // Scale pulse on hover
          if (hovered) {
            const scalePulse = 1 + Math.sin(state.clock.elapsedTime * 3 + i * 0.5) * 0.05;
            antenna.scale.set(scalePulse, scalePulse, scalePulse);
          }
        }
      });
    }
  });

  return (
    <group
      ref={groupRef}
      position={[0, 0, 0]}
      rotation={rotation}
    >
      {/* Dynamic point light for antenna glow - reduced on mobile */}
      {!isMobile && (
        <>
          <pointLight
            position={[0.8, 0.2, 0.2]}
            intensity={hovered ? 2 : 0.8}
            distance={3}
            color="#ff6b00"
          />

          {/* Accent rim light */}
          <pointLight
            position={[-1, 0, 0.5]}
            intensity={0.5}
            distance={2}
            color="#00d4ff"
          />
        </>
      )}
      {/* ============================================ */}
      {/* BOTTOM LAYER: Technical Base (PCB substrate) */}
      {/* ============================================ */}
      <mesh position={[0, 0, -0.045]} castShadow receiveShadow>
        <boxGeometry args={[3.4, 2.1, 0.04]} />
        <meshStandardMaterial
          color="#1a3a1a"
          roughness={0.8}
          metalness={0.2}
        />
      </mesh>

      {/* ============================================ */}
      {/* TECHNICAL COMPONENTS LAYER */}
      {/* ============================================ */}

      {/* NFC Chip - MORE PROMINENT */}
      <mesh position={[0.8, -0.3, -0.02]} castShadow>
        <boxGeometry args={[0.35, 0.35, 0.03]} />
        <meshStandardMaterial
          color="#1a1a1a"
          metalness={0.95}
          roughness={0.15}
          emissive="#2a2a2a"
          emissiveIntensity={0.3}
        />
      </mesh>

      {/* Chip center detail */}
      <mesh position={[0.8, -0.3, -0.005]}>
        <boxGeometry args={[0.15, 0.15, 0.01]} />
        <meshStandardMaterial
          color="#0a0a0a"
          metalness={1}
          roughness={0.1}
        />
      </mesh>

      {/* Chip pins/contacts - ENHANCED */}
      {[-0.14, -0.06, 0.02, 0.10].map((offset, i) => (
        <mesh key={`pin-${i}`} position={[0.8 + offset, -0.48, -0.02]} castShadow>
          <boxGeometry args={[0.035, 0.1, 0.015]} />
          <meshStandardMaterial
            color="#d4af37"
            emissive="#ff9500"
            emissiveIntensity={0.3}
            metalness={1}
            roughness={0.05}
          />
        </mesh>
      ))}

      {/* NFC Antenna Coil (multiple rings) - THICKER & MORE PROMINENT */}
      {(isMobile ? [0.5, 0.8, 1.1] : [0.5, 0.65, 0.8, 0.95, 1.1]).map((radius, i) => (
        <mesh
          key={`antenna-${i}`}
          ref={(el) => (antennaRefs.current[i] = el)}
          position={[0.8, 0.2, -0.02]}
          rotation={[0, 0, 0]}
          castShadow={!isMobile}
        >
          <torusGeometry args={[radius, 0.035, isMobile ? 8 : 12, isMobile ? 32 : 64]} />
          <meshStandardMaterial
            color="#d4af37"
            emissive="#ff6b00"
            emissiveIntensity={hovered ? 1.5 : 0.5}
            metalness={0.95}
            roughness={0.1}
            toneMapped={false}
          />
        </mesh>
      ))}

      {/* Outer glow ring for dramatic effect */}
      <mesh position={[0.8, 0.2, -0.02]} rotation={[0, 0, 0]}>
        <torusGeometry args={[1.25, 0.02, 12, 64]} />
        <meshStandardMaterial
          color="#ff6b00"
          emissive="#ff6b00"
          emissiveIntensity={hovered ? 2 : 0.8}
          transparent={true}
          opacity={hovered ? 0.6 : 0.3}
          toneMapped={false}
        />
      </mesh>

      {/* Connection traces/wires from chip to antenna - ENHANCED GLOW */}
      <mesh position={[0.8, -0.15, -0.02]} castShadow>
        <boxGeometry args={[0.04, 0.3, 0.008]} />
        <meshStandardMaterial
          color="#d4af37"
          emissive="#ff9500"
          emissiveIntensity={0.5}
          metalness={1}
          roughness={0.05}
        />
      </mesh>
      <mesh position={[0.95, -0.15, -0.02]} castShadow>
        <boxGeometry args={[0.04, 0.3, 0.008]} />
        <meshStandardMaterial
          color="#d4af37"
          emissive="#ff9500"
          emissiveIntensity={0.5}
          metalness={1}
          roughness={0.05}
        />
      </mesh>

      {/* Circuit traces (decorative) - MORE GLOW */}
      {(isMobile
        ? [[-1.2, 0.5, 0.8, 0.025], [1.2, 0.7, 0.4, 0.025]]
        : [[-1.2, 0.5, 0.8, 0.025], [-0.8, -0.6, 0.6, 0.025], [-1.4, -0.2, 0.5, 0.025], [1.2, 0.7, 0.4, 0.025]]
      ).map(([x, y, length, width], i) => (
        <mesh key={`trace-${i}`} position={[x, y, -0.02]} castShadow={!isMobile}>
          <boxGeometry args={[width, length, 0.008]} />
          <meshStandardMaterial
            color="#4a9eff"
            emissive="#00d4ff"
            emissiveIntensity={isMobile ? 0.4 : 0.6}
            metalness={0.8}
            roughness={0.2}
            toneMapped={false}
          />
        </mesh>
      ))}

      {/* Small capacitors/resistors - skip on mobile for performance */}
      {!isMobile && [
        [-1.0, 0.5, 0.15, 0.08],
        [-0.6, -0.6, 0.12, 0.06],
        [1.1, 0.6, 0.1, 0.05],
      ].map(([x, y, w, h], i) => (
        <mesh key={`component-${i}`} position={[x, y, -0.015]}>
          <boxGeometry args={[w, h, 0.03]} />
          <meshStandardMaterial color="#1a1a1a" metalness={0.4} roughness={0.6} />
        </mesh>
      ))}

      {/* ============================================ */}
      {/* TOP LAYER: Transparent Designed Card Surface */}
      {/* ============================================ */}
      <mesh position={[0, 0, 0.01]} castShadow receiveShadow>
        <boxGeometry args={[3.4, 2.1, 0.05]} />
        <meshPhysicalMaterial
          color={color}
          metalness={0.95}
          roughness={0.05}
          clearcoat={1}
          clearcoatRoughness={0.05}
          transparent={true}
          opacity={0.65}
          transmission={0.3}
          thickness={0.8}
          ior={1.5}
          envMapIntensity={2}
          reflectivity={0.9}
          specularIntensity={1}
          specularColor="#ffffff"
        />
      </mesh>

      {/* Card surface design elements (logo area) - MORE PROMINENT */}
      <mesh position={[-1.0, 0.5, 0.03]}>
        <planeGeometry args={[0.7, 0.7]} />
        <meshStandardMaterial
          color="#ffffff"
          emissive="#ffffff"
          emissiveIntensity={0.1}
          transparent={true}
          opacity={0.15}
          metalness={0.9}
          roughness={0.1}
        />
      </mesh>

      {/* Design accent lines - THICKER & MORE VISIBLE */}
      {[
        [-1.2, -0.7, 1.8, 0.02],
        [-1.2, -0.8, 1.8, 0.02],
        [-1.2, -0.75, 1.8, 0.005],
      ].map(([x, y, width, height], i) => (
        <mesh key={`accent-${i}`} position={[x, y, 0.03]}>
          <planeGeometry args={[width, height]} />
          <meshStandardMaterial
            color="#ffffff"
            emissive="#00d4ff"
            emissiveIntensity={0.2}
            transparent={true}
            opacity={0.25}
            metalness={0.95}
            roughness={0.05}
          />
        </mesh>
      ))}

      {/* Additional tech pattern details */}
      {[
        [0.5, 0.8, 0.3, 0.01],
        [0.5, 0.6, 0.25, 0.01],
        [-0.5, -0.5, 0.4, 0.01],
      ].map(([x, y, width, height], i) => (
        <mesh key={`detail-${i}`} position={[x, y, 0.03]}>
          <planeGeometry args={[width, height]} />
          <meshStandardMaterial
            color="#00d4ff"
            emissive="#00d4ff"
            emissiveIntensity={0.3}
            transparent={true}
            opacity={0.2}
            metalness={0.9}
            roughness={0.1}
          />
        </mesh>
      ))}

      {/* ============================================ */}
      {/* CARD EDGE (shows the layers) */}
      {/* ============================================ */}
      {/* This creates a nice cross-section effect on the right edge */}
      <mesh position={[1.71, 0, -0.02]}>
        <boxGeometry args={[0.02, 2.1, 0.08]} />
        <meshStandardMaterial
          color="#2a2a2a"
          metalness={0.3}
          roughness={0.7}
        />
      </mesh>

      {/* Edge highlight to show the layering */}
      <mesh position={[1.72, 0, 0.01]}>
        <boxGeometry args={[0.01, 2.1, 0.04]} />
        <meshStandardMaterial
          color={color}
          transparent={true}
          opacity={0.8}
          metalness={0.9}
          roughness={0.1}
        />
      </mesh>
    </group>
  );
}
